import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { warningAccentBorderStyle } from "@/components/ui/feedback/warningVisual";

export type KpiCardAccent = "primary" | "success" | "warning" | "danger" | "info" | "none";

export type KpiCardProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  label: ReactNode;
  value: ReactNode;
  accent?: KpiCardAccent;
  labelClassName?: string;
  valueClassName?: string;
};

const borderL = (token: string): CSSObject => ({
  "--tw-border-opacity": "1",
  borderLeftColor: `hsl(var(--bfrs-color-${token}) / var(--tw-border-opacity, 1))`
});

const kpiAccentStyles: Record<KpiCardAccent, CSSObject> = {
  primary: borderL("primary"),
  success: borderL("success"),
  warning: {},
  danger: borderL("danger"),
  info: borderL("info"),
  none: borderL("border")
};

const KpiCardRoot = styled("div", {
  label: "AgenticKpiCard-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { accent: KpiCardAccent } }>(({ ownerState }) => [
  {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: ".375rem",
    borderRadius: "var(--bfrs-radius-lg)",
    borderTopWidth: "1px",
    borderBottomWidth: "1px",
    borderRightWidth: "1px",
    borderLeftWidth: "3px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    paddingTop: "8px",
    paddingBottom: "8px"
  },
  kpiAccentStyles[ownerState.accent]
]);

const KpiCardLabel = styled("span", {
  label: "AgenticKpiCard-label"
})({
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "1.5",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const KpiCardValue = styled("span", {
  label: "AgenticKpiCard-value"
})({
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "1.5",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

export const KpiCard = forwardRef<HTMLDivElement, KpiCardProps>(
  ({ className, labelClassName, valueClassName, label, value, accent = "primary", style, ...props }, ref) => (
    <KpiCardRoot
      ref={ref}
      ownerState={{ accent }}
      className={className}
      style={{ ...(accent === "warning" ? warningAccentBorderStyle : undefined), ...style }}
      {...props}
    >
      <KpiCardLabel className={labelClassName}>{label}</KpiCardLabel>
      <KpiCardValue className={valueClassName}>{value}</KpiCardValue>
    </KpiCardRoot>
  )
);

KpiCard.displayName = "KpiCard";
